import { ParsedUrlQuery } from 'querystring';
import React, { ReactNode, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { NextPageContext } from 'next';
import { useRouter } from 'next/router';
import mapValues from 'lodash/mapValues';
import times from 'lodash/times';
import { Link as ScrollLink } from 'react-scroll/modules';
import { Element } from 'react-scroll';
import isEmpty from 'lodash/isEmpty';
import classNames from 'classnames';
import Container from '../components/grid/Container';
import HorizontalCardList, {
  VisibleCards
} from '../components/common/HorizontalCardList';
import DestinationCard from '../components/destinations/DestinationCard';
import MemoryCard from '../components/memories/MemoryCard';
import { t } from '../utils/i18n';
import Spacing from '../components/common/Spacing';
import SEO from '../components/head/SEO';
import { generatePath } from '../urls/generatePath';
import { Paths } from '../urls/Paths';
import HorizontalLine from '../components/common/HorizontalLine';
import Row from '../components/grid/Row';
import Col from '../components/grid/Col';
import { navbarLayout } from '../layouts/navbarLayout';
import H from '../components/elements/H';
import Breadcrumbs from '../components/head/Breadcrumbs';
import RenderAsync from '../components/common/RenderAsync';
import {
  searchResultActions,
  selectSearchResults
} from '../redux/features/searchResults';
import { RootState } from '../redux/features';
import HeadingWithCount from '../components/common/HeadingWithCount';
import { Answers } from '../search/SearchAnswers';
import SearchResultsFilters from '../components/search-filter/SearchResultsFilters';
import { AnswerOption, Question } from '../redux/features/questions';
import generatePathWithQueryString from '../urls/generatePathWithQueryString';
import { baseXUnit, breakpoints, columnTopSpacing } from '../styles/theme';
import { BoxSkeleton } from '../components/common/skeletons';
import FadeAnimation from '../components/animations/FadeAnimation';
import DisplayOn from '../components/common/DisplayOn';
import StickyCol from '../components/grid/StickyCol';
import SummaryHeading from '../components/search/SummaryHeading';
import { CmsMemory } from '../integrations/contentful/types';
import { DEFAULT_NAVBAR_ANCHOR_OFFSET_IN_PIXELS } from '../components/navigation/DefaultNavbar';
import { arrayHead } from '../utils/array';
import HyperLink from '../components/elements/HyperLink';
import { trackAnalyticsEvent } from '../analytics/analytics';
import TimMessage from '../components/tim/TimMessage';
import TextIconActionCard from '../components/common/TextIconActionCard';
import { LoadingState } from '../loading/loadable';
import { TimPage } from './TimPage';

interface SearchResultsPageProps {
  searchAnswers: Answers;
}

const ANALYTICS_CATEGORY = 'Suositukset-sivu';

const SearchResultsPage: TimPage<SearchResultsPageProps> = ({
  searchAnswers
}) => {
  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(
      searchResultActions.fetchSearchResultsIfNotFetched({
        searchAnswers: searchAnswers
      })
    );
  }, [dispatch, searchAnswers]);
  const searchResultsLoadable = useSelector((state: RootState) =>
    selectSearchResults(state, searchAnswers)
  );
  const router = useRouter();
  const noMemories =
    searchResultsLoadable &&
    searchResultsLoadable.loadingState === LoadingState.DONE &&
    searchResultsLoadable.results.memories.length === 0;

  const updateQueryParams = (
    question: Question,
    answer: AnswerOption | null
  ) => {
    const newSearchAnswers = {
      ...searchAnswers,
      [question.key]: answer ? [answer.value] : []
    };
    trackAnalyticsEvent(
      ANALYTICS_CATEGORY,
      'Vastattu chattiin',
      JSON.stringify(newSearchAnswers)
    );
    const url = generatePathWithQueryString(
      Paths.SearchResults,
      newSearchAnswers
    );
    router.push(url);
  };

  const renderResultsCount = (type: 'destination' | 'memory') => {
    return (
      <RenderAsync
        state={searchResultsLoadable && searchResultsLoadable.loadingState}
        onLoading={renderEmptyElement}
      >
        {() => (
          <FadeAnimation>
            {type === 'memory'
              ? searchResultsLoadable.results.memories.length
              : searchResultsLoadable.results.destinations.length}
          </FadeAnimation>
        )}
      </RenderAsync>
    );
  };

  const renderTimMemoryMessage = (memoriesCount: number) => {
    if (memoriesCount > 0) {
      return t('Nämä matkaajien unohtumattomat matkat valitsin sinulle 🥳');
    }
    return t(
      'Näillä hakuehdoilla meillä ei vielä ole tarjolla matkoja sinulle 😬'
    );
  };

  const renderMemoryCards = (cmsMemories: CmsMemory[], columns: boolean) => {
    return cmsMemories
      .filter((cmsMemory) => !!arrayHead(cmsMemory.fields.travellers))
      .map((cmsMemory) => {
        const {
          mainImage,
          slug,
          startDate,
          endDate,
          title,
          subtitle,
          matkanKesto,
          golfInfo,
          tripDepartures,
          vehicleInfo
        } = cmsMemory.fields;
        const traveller = arrayHead(cmsMemory.fields.travellers);
        const renderMemoryCard = () => {
          if (
            !mainImage ||
            !traveller ||
            (!traveller.fields.profilePicture && !subtitle)
          ) {
            return null;
          }
          return (
            <MemoryCard
              key={cmsMemory.sys.id}
              memory={{
                mainImage,
                slug,
                startDate,
                endDate,
                title,
                subtitle,
                matkanKesto,
                golfInfo,
                tripDepartures,
                vehicleInfo
              }}
              traveller={{
                name: traveller.fields.title,
                profilePicture: traveller.fields.profilePicture,
                slug: traveller.fields.slug
              }}
            />
          );
        };
        if (columns) {
          return (
            <Col size={[0, 12, 12]} key={cmsMemory.sys.id}>
              <Spacing bottom={[0, 5, 5]}>{renderMemoryCard()}</Spacing>
            </Col>
          );
        } else {
          return renderMemoryCard();
        }
      });
  };

  return (
    <div data-cy="search-results-page">
      <SEO
        title={t('Tim suosittelee')}
        canonicalPath={generatePath(Paths.SearchResults)}
        description={t(
          'Vastaamalla muutamaan kysymykseen, jätät matkakohteen, lennon sekä majoituksen valinnan VGM:n huoleksi. VGM on täällä sinua varten.'
        )}
        /* There are lot of search answer combinations and it's not good idea to let index all of those.
           If we allow indexing all combinations (or some recommendations), we should also customize title & maybe description accordingly.
            "They allow Google to index the content of the /s/ folder,
             which means they are indexing a lot of their internal search results people might have just looked for just once.
             This is actually SEO from the 2007-era" - https://www.sistrix.com/blog/2009-vs-2019-seo-how-hometogo-overtook-airbnb/"
         */
        noindex={Object.keys(searchAnswers).length > 0}
      />
      <Breadcrumbs
        items={[
          {
            name: t('Tim suosittelee'),
            path: Paths.SearchResults
          }
        ]}
      />
      <Spacing top={[9, 9, 0]} bottom={[4, 4, 0]}>
        <DisplayOn small medium>
          <SearchResultsFilters
            searchAnswers={searchAnswers}
            onUpdate={updateQueryParams}
          />
          <HorizontalLine expand={false} />
        </DisplayOn>
      </Spacing>
      <Container>
        <Row>
          <Col size={[0, 0, 7]}>
            <StickyCol contentTopOffset={columnTopSpacing.sideColumn}>
              <DisplayOn large>
                <Spacing bottom={4}>
                  <H styling="h2">
                    <strong>{t('Tim suosittelee')}</strong>
                  </H>
                </Spacing>
                <SearchResultsFilters
                  searchAnswers={searchAnswers}
                  onUpdate={updateQueryParams}
                />
                <Spacing top={5} bottom={2}>
                  <ScrollLink
                    to="memories"
                    offset={DEFAULT_NAVBAR_ANCHOR_OFFSET_IN_PIXELS}
                    smooth
                  >
                    <SummaryHeading
                      text={<HyperLink tag="div">{t('Matkaa')}</HyperLink>}
                      renderCount={() => renderResultsCount('memory')}
                    />
                  </ScrollLink>
                </Spacing>
                <ScrollLink
                  to="destinations"
                  offset={DEFAULT_NAVBAR_ANCHOR_OFFSET_IN_PIXELS}
                  smooth
                >
                  <SummaryHeading
                    text={<HyperLink tag="div">{t('Matkakohdetta')}</HyperLink>}
                    renderCount={() => renderResultsCount('destination')}
                  />
                </ScrollLink>
              </DisplayOn>
            </StickyCol>
          </Col>
          <Col size={[0, 0, 1]}></Col>
          <Col size={[24, 24, 16]}>
            <Spacing top={[0, 0, columnTopSpacing.contentColumn]}>
              <Element name="memories">
                <HeadingWithCount
                  icon={'travel-memory'}
                  text={t('Matkat')}
                  renderCount={() => renderResultsCount('memory')}
                  data-testid="memory-count"
                />
              </Element>
            </Spacing>
            {!isEmpty(searchAnswers) && (
              <Spacing top={[1, 2, 2]} left={[0, 0.5, 0.5]}>
                <RenderAsync
                  state={
                    searchResultsLoadable && searchResultsLoadable.loadingState
                  }
                  onLoading={() => {
                    return (
                      <BoxSkeleton
                        uniqueKey={
                          'search-results-tim-memory-memories-skeleton'
                        }
                        height={[baseXUnit(4), baseXUnit(3), baseXUnit(3)]}
                      />
                    );
                  }}
                >
                  {() => (
                    <TimMessage data-cy="search-results-tim-memories-message">
                      {renderTimMemoryMessage(
                        searchResultsLoadable.results.memories.length
                      )}
                    </TimMessage>
                  )}
                </RenderAsync>
              </Spacing>
            )}
            <Spacing top={[2, 5, 5]} bottom={[2, 2, 2]}>
              <div
                className={classNames('memories', {
                  'zero-memories': noMemories
                })}
              >
                <RenderAsync
                  state={
                    searchResultsLoadable && searchResultsLoadable.loadingState
                  }
                  onLoading={renderMemoriesSkeleton}
                >
                  {() => (
                    <>
                      <DisplayOn small>
                        <HorizontalCardList
                          visibleCards={VisibleCards.One}
                          expand
                        >
                          {renderMemoryCards(
                            searchResultsLoadable.results.memories,
                            false
                          )}
                        </HorizontalCardList>
                      </DisplayOn>
                      <DisplayOn medium large>
                        <Row>
                          {renderMemoryCards(
                            searchResultsLoadable.results.memories,
                            true
                          )}
                        </Row>
                      </DisplayOn>
                      {searchResultsLoadable.results.memories.length === 0 && (
                        <Row>
                          <Col size={[24, 12, 12]}>
                            <Spacing bottom={[0, 7, 7, 7]}>
                              <TextIconActionCard
                                bottomText={t('Poista suodatukset')}
                                bottomIcon="nav-close-small"
                                routeLinkPath={Paths.SearchResults}
                                data-cy="search-results-page-reset-filters-action-card"
                              >
                                <H styling="h4" tag="div" data>
                                  {t(
                                    'Kokeile muita vaihtoehtoja tai poista kaikki suodaukset.'
                                  )}
                                </H>
                              </TextIconActionCard>
                            </Spacing>
                          </Col>
                        </Row>
                      )}
                    </>
                  )}
                </RenderAsync>
              </div>
            </Spacing>
            <HorizontalLine />
            <Spacing top={[4, 5, 5]}>
              <Element name="destinations">
                <HeadingWithCount
                  icon={'travel-pin'}
                  text={t('Matkakohteet')}
                  renderCount={() => renderResultsCount('destination')}
                  data-testid="destination-count"
                />
              </Element>
            </Spacing>
            {!isEmpty(searchAnswers) && (
              <Spacing top={[1, 2, 2]} left={[0, 0.5, 0.5]}>
                <TimMessage>
                  {t(
                    'Euroopan kohteita makuusi. 👏 Inspiroidu ja luo oma matkasi!'
                  )}
                </TimMessage>
              </Spacing>
            )}
            <Spacing top={[3, 5, 5]} bottom={[10, 10, 10]}>
              <RenderAsync
                state={
                  searchResultsLoadable && searchResultsLoadable.loadingState
                }
                onLoading={renderDestinationsSkeleton}
              >
                {() => (
                  <Row>
                    {searchResultsLoadable.results.destinations.map(
                      (destination) => {
                        return (
                          <Col
                            data-testid="destination"
                            key={destination.sys.id}
                            size={[12, 6, 6]}
                          >
                            {destination.fields.mainImage && (
                              <DestinationCard
                                country={destination.fields.country}
                                mainImage={destination.fields.mainImage}
                                name={destination.fields.title}
                                slug={destination.fields.slug}
                              />
                            )}
                          </Col>
                        );
                      }
                    )}
                  </Row>
                )}
              </RenderAsync>
            </Spacing>
          </Col>
        </Row>
      </Container>
      {/*language=CSS*/}
      <style jsx>{`
        @media ${breakpoints.small} {
          .memories:not(.zero-memories) {
            /* Keep destination title in same position after page is loaded and skeleton is disappeared. */
            min-height: ${baseXUnit(36)};
          }
        }
      `}</style>
    </div>
  );
};

SearchResultsPage.getLayout = (page) =>
  navbarLayout(
    <DisplayOn small medium>
      <H styling="h3" tag="div">
        <strong>{t('Tim suosittelee')}</strong>
      </H>
    </DisplayOn>,
    page
  );

SearchResultsPage.getInitialProps = async function ({
  query
}: NextPageContext) {
  const searchAnswers = castValuesToArray(query) as Answers;
  return { searchAnswers: searchAnswers };
};

function castValuesToArray(query: ParsedUrlQuery) {
  return mapValues(query, (value) => {
    // We already have array when there is multiple values for one key, for example &activites=party&activities=chill.
    return Array.isArray(value) ? value : [value];
  });
}

const renderMemoriesSkeleton = (): ReactNode => {
  const renderMemorySkeleton = (index: number) => (
    <>
      {/* Image */}
      <BoxSkeleton
        uniqueKey={'memory-skeleton-image-' + index}
        height={baseXUnit(20.5)}
      />
      <Spacing top={1}></Spacing>
      {/* Text */}
      <BoxSkeleton
        uniqueKey={'memory-skeleton-text-' + index}
        height={baseXUnit(12)}
      />
    </>
  );
  const renderDesktopMemorySkeleton = (index: number) => (
    <Col size={[0, 12, 12]} key={index}>
      <Spacing bottom={[0, 3, 5]}>
        {/* Image */}
        <BoxSkeleton
          uniqueKey={'memory-desktop-skeleton-image-' + index}
          height={baseXUnit(22)}
        />
        <Spacing top={1}></Spacing>
        {/* Text */}
        <BoxSkeleton
          uniqueKey={'memory-desktop-skeleton-text-' + index}
          height={baseXUnit(14)}
        />
      </Spacing>
    </Col>
  );
  return (
    <>
      <DisplayOn small>
        <HorizontalCardList visibleCards={VisibleCards.One} expand>
          {renderMemorySkeleton(1)}
          {renderMemorySkeleton(2)}
        </HorizontalCardList>
      </DisplayOn>
      <DisplayOn medium large>
        <Row>
          {times(8, (index) => {
            return renderDesktopMemorySkeleton(index);
          })}
        </Row>
      </DisplayOn>
    </>
  );
};

const renderDestinationsSkeleton = (): ReactNode => {
  const renderDestinationSkeleton = (index: number) => (
    <Col size={12}>
      <Spacing bottom={5}>
        {/* Image */}
        <BoxSkeleton
          uniqueKey={'destination-image-skeleton-' + index}
          height={baseXUnit(16.5)}
        />
        <Spacing top={1}></Spacing>
        {/* Text */}
        <BoxSkeleton
          uniqueKey={'destination-text-skeleton-' + index}
          height={baseXUnit(5)}
        />
      </Spacing>
    </Col>
  );

  return (
    <DisplayOn small>
      <Row>
        {renderDestinationSkeleton(1)}
        {renderDestinationSkeleton(2)}
        {renderDestinationSkeleton(3)}
        {renderDestinationSkeleton(4)}
      </Row>
    </DisplayOn>
  );
};

const renderEmptyElement = (): ReactNode => {
  return <></>;
};

export default SearchResultsPage;
